import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import MarketingInterviewProcessSection from './sections/MarketingInterviewProcessSection';
import HowJumpstartWorksSection from './sections/HowJumpstartWorksSection';
import SetupForSuccessSection from './sections/SetupForSuccessSection';
import { useAuthRedirect } from '../../hooks/useAuthRedirect';
import MarketingInterviewSupportSection from './sections/MarketingInterviewSupportSection';
import MarketingStandOutSection from './sections/MarketingStandOutSection';
import MarketingCaseStudySection from './sections/MarketingCaseStudySection';
import MarketingFinalInterviewSection from './sections/MarketingFinalInterviewSection';
import MarketingOffersEndgameSection from './sections/MarketingOffersEndgameSection';
import StandoutApplicationsSection from './sections/StandoutApplicationsSection';
import { trackEvent } from '../../lib/analytics';

const MarketingResourcesHub = () => {
  const [currentSection, setCurrentSection] = useState('main');
  const [completedSections, setCompletedSections] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ensure user is authenticated
  const { isAuthenticated, isChecking } = useAuthRedirect();

  const sections = [
    {
      id: 'how-jumpstart-works',
      title: 'How Jumpstart Works',
      description: 'Understand roles vs match requests, deadlines, and weekly cadence.',
      icon: '📅',
      estimatedTime: '8 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'setup-for-success',
      title: 'Setting yourself up for success',
      description: 'Polish your profile, level up your CV, and create a standout video intro.',
      icon: '🧭',
      estimatedTime: '15 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'marketing-interview-process',
      title: 'Marketing interviews at startups: the essentials',
      description: 'Whilst every company is a bit different, here\'s the common flow and what early-stage startups really care about.',
      icon: '🎯',
      estimatedTime: '15 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'marketing-interview-support',
      title: 'Interview practice & support',
      description: 'Jumpstart gives you access to tailored interview prep – make use of it!',
      icon: '🤝',
      estimatedTime: '10 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'marketing-stand-out',
      title: 'First interview: stand out early',
      description: 'Master the art of first impressions and position yourself as the ideal marketing candidate.',
      icon: '⭐',
      estimatedTime: '20 min read',
      difficulty: 'Intermediate'
    },
    {
      id: 'marketing-case-study',
      title: 'The take-home case study',
      description: 'Turn your case study into a showcase of your strategic thinking and execution skills.',
      icon: '📊',
      estimatedTime: '25 min read',
      difficulty: 'Advanced'
    },
    {
      id: 'standout-applications',
      title: 'How to Make Standout Applications',
      description: 'Tips to write winning rationales, use startup video pitches and smart outreach.',
      icon: '✍️',
      estimatedTime: '10 min read',
      difficulty: 'Beginner'
    },
    {
      id: 'marketing-final-interview',
      title: 'Final interviews: sealing the deal',
      description: 'Navigate culture fit and self-awareness questions to close the deal.',
      icon: '🏆',
      estimatedTime: '18 min read',
      difficulty: 'Intermediate'
    },
    {
      id: 'marketing-offers-endgame',
      title: 'Offers & what comes next',
      description: 'Navigate offers, negotiate terms, and make the right career decision.',
      icon: '🎉',
      estimatedTime: '12 min read',
      difficulty: 'Advanced'
    }
  ];

  // Initialize section from URL param and react to URL changes
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    const sectionIds = sections.map(s => s.id);
    if (!sectionParam) {
      if (currentSection !== 'main') {
        setCurrentSection('main');
      }
      return;
    }
    if (sectionIds.includes(sectionParam) && currentSection !== sectionParam) {
      setCurrentSection(sectionParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setSection = (sectionId) => {
    setCurrentSection(sectionId);
    try {
      const params = new URLSearchParams(searchParams);
      if (sectionId === 'main') {
        if (params.has('section')) {
          params.delete('section');
          setSearchParams(params, { replace: false });
        }
      } else {
        if (params.get('section') !== sectionId) {
          params.set('section', sectionId);
          setSearchParams(params, { replace: false });
        }
      }
    } catch (_) {}
  };

  // Fetch candidate progress
  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const res = await axios.get('/api/candidates/fetchCandidate', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data && res.data.candidate) {
          const candidateData = res.data.candidate.fields;
          
          // Debug: Log all available fields to find correct field names for progress tracking
          console.log("Marketing - Available candidate fields:", Object.keys(candidateData));
          console.log("Marketing - Looking for progress fields:", 
            Object.keys(candidateData).filter(key => 
              key.toLowerCase().includes('interview') || 
              key.toLowerCase().includes('process') ||
              key.toLowerCase().includes('marketing') ||
              key.toLowerCase().includes('stand') ||
              key.toLowerCase().includes('case') ||
              key.toLowerCase().includes('final') ||
              key.toLowerCase().includes('offer')
            )
          );
          
          const completed = sections
            .filter(section => candidateData[section.id])
            .map(section => section.id);
          
          setCompletedSections(completed);
        }
      } catch (error) {
        console.error("Error fetching candidate progress:", error);
      }
    };

    fetchProgress();
  }, [isAuthenticated, isChecking]);

  const markAsCompleted = async (sectionId) => {
    if (completedSections.includes(sectionId)) return;

    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("No token found");
        return;
      }
      
      await axios.patch('/api/candidates/updateCandidate', 
        { fields: { [sectionId]: true } },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = [...completedSections, sectionId];
      setCompletedSections(updated);
    } catch (error) {
      console.error("Error updating candidate progress:", error);
    }
  };

  // Navigation helper
  const getNextSection = (currentId) => {
    const sectionIds = sections.map(section => section.id);
    const currentIndex = sectionIds.indexOf(currentId);
    return currentIndex < sectionIds.length - 1 ? sectionIds[currentIndex + 1] : null;
  };

  const goToNextSection = (currentId) => {
    const nextSectionId = getNextSection(currentId);
    if (nextSectionId) {
      setSection(nextSectionId);
    } else {
      setSection('main');
    }
    // Scroll to top of page when navigating to next section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSectionContent = () => {
    const getSectionTitle = (id) => sections.find(s => s.id === id)?.title || '';
    const nextSectionId = getNextSection(currentSection);
    const nextSectionTitle = nextSectionId ? getSectionTitle(nextSectionId) : null;

    const commonProps = {
      onBack: () => setSection('main'),
      onNext: () => goToNextSection(currentSection),
      nextSectionTitle,
      hasNext: !!nextSectionId
    };

    switch (currentSection) {
      case 'how-jumpstart-works':
        return (
          <HowJumpstartWorksSection
            {...commonProps}
            onComplete={() => markAsCompleted('how-jumpstart-works')}
            isCompleted={completedSections.includes('how-jumpstart-works')}
          />
        );
      case 'setup-for-success':
        return (
          <SetupForSuccessSection
            {...commonProps}
            onComplete={() => markAsCompleted('setup-for-success')}
            isCompleted={completedSections.includes('setup-for-success')}
            streamType={'marketing'}
            exampleVideos={[
              { name: 'Natasha', url: 'https://www.loom.com/share/ae43c5b099b242c698d4a063c9a98852?sid=e242de45-1704-4819-8279-59b27a83ccf2', creator: 'Uses past experiences to convey passion with a natural tone' }
            ]}
          />
        );
      case 'marketing-interview-process':
        return (
          <MarketingInterviewProcessSection 
            {...commonProps}
            onComplete={() => markAsCompleted('marketing-interview-process')}
            isCompleted={completedSections.includes('marketing-interview-process')}
          />
        );
      case 'marketing-interview-support':
        return (
          <MarketingInterviewSupportSection 
            {...commonProps}
            onComplete={() => markAsCompleted('marketing-interview-support')}
            isCompleted={completedSections.includes('marketing-interview-support')}
          />
        );
      case 'marketing-stand-out':
        return (
          <MarketingStandOutSection 
            {...commonProps}
            onComplete={() => markAsCompleted('marketing-stand-out')}
            isCompleted={completedSections.includes('marketing-stand-out')}
          />
        );
      case 'marketing-case-study':
        return (
          <MarketingCaseStudySection 
            {...commonProps}
            onComplete={() => markAsCompleted('marketing-case-study')}
            isCompleted={completedSections.includes('marketing-case-study')}
          />
        );
      case 'standout-applications':
        return (
          <StandoutApplicationsSection 
            {...commonProps}
            onComplete={() => markAsCompleted('standout-applications')}
            isCompleted={completedSections.includes('standout-applications')}
          />
        );
      case 'marketing-final-interview':
        return (
          <MarketingFinalInterviewSection 
            {...commonProps}
            onComplete={() => markAsCompleted('marketing-final-interview')}
            isCompleted={completedSections.includes('marketing-final-interview')}
          />
        );
      case 'marketing-offers-endgame':
        return (
          <MarketingOffersEndgameSection 
            {...commonProps}
            onComplete={() => markAsCompleted('marketing-offers-endgame')}
            isCompleted={completedSections.includes('marketing-offers-endgame')}
          />
        );
      default:
        return null;
    }
  };

  if (currentSection !== 'main') {
    return renderSectionContent();
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Compact Header */}
      <div className="w-full bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Marketing Interview Resources
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Master every stage of the startup marketing interview process with our comprehensive guides and expert insights.
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Compact Progress Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        
        {/* Compact coaching banner */}
        <div className="mb-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#02B491] text-white rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">🎓</span>
              <div>
                <h3 className="font-semibold text-sm">Book 1-on-1 interview prep sessions</h3>
                <p className="text-xs text-white/90">Get personalised coaching from our expert marketing interview specialists</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href="https://calendly.com/d/cw8r-882-477/jumpstart-marketing-first-interview-prep-call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-[#02B491] rounded-md hover:bg-gray-50 transition-colors"
              >
                Book First Interview Prep
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a
                href="https://calendly.com/d/cw92-wkh-sxj/jumpstart-marketing-2nd-interview-preparationiew"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 text-white border border-white/20 rounded-md hover:bg-white/20 transition-colors"
              >
                Second Round Prep
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <a
                href="https://calendly.com/kabir-7/catch-up-regarding-jumpstart?month=2025-07"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 text-white border border-white/20 rounded-md hover:bg-white/20 transition-colors"
              >
                Final Round Prep
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
        
        {/* Compact Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((section, index) => {
            const isCompleted = completedSections.includes(section.id);
            
            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl border-2 transition-all duration-300 hover:shadow-lg cursor-pointer group relative overflow-hidden ${
                  isCompleted 
                    ? 'border-[#02B491] shadow-md' 
                    : 'border-gray-100 hover:border-[#02B491]/50 shadow-sm hover:shadow-md'
                }`}
                onClick={() => {
                  try {
                    trackEvent('Resource Card Clicked', {
                      card_id: section.id,
                      card_title: section.title,
                      hub: 'marketing_resources',
                    });
                  } catch (_) {}
                  setSection(section.id);
                }}
              >
                <div className="p-6">
                  {/* Header with Icon and Title */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-2xl">{section.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#02B491] transition-colors">
                      {section.title}
                    </h3>
                    {isCompleted && (
                      <div className="ml-auto">
                        <div className="w-6 h-6 bg-[#02B491] rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {section.description}
                  </p>

                  {/* Start Button with Arrow */}
                  <div className="flex items-center justify-between">
                    <span className="text-[#02B491] font-semibold group-hover:text-[#029078] transition-colors">
                      Start
                    </span>
                    <svg 
                      className="w-5 h-5 text-[#02B491] group-hover:text-[#029078] group-hover:translate-x-1 transition-all duration-200" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>

                {/* Gradient overlay for completed sections */}
                {isCompleted && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#02B491]/5 to-transparent pointer-events-none" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MarketingResourcesHub; 